import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, RotateCcwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { FieldDefinition, LoanRecord, LoanTag } from '../../types/highlights';
import { palette, paletteOrder } from '../../utils/palette';
import { isTagVisible } from '../../utils/rules';
import { ConditionFields } from './ConditionFields';
interface TagsEditorProps {
  open: boolean;
  tags: LoanTag[];
  catalog: FieldDefinition[];
  byId: Map<string, FieldDefinition>;
  loan: LoanRecord;
  onAddTag: () => void;
  onUpdateTag: (tagId: string, patch: Partial<LoanTag>) => void;
  onRemoveTag: (tagId: string) => void;
  onMoveTag: (from: number, to: number) => void;
  onResetTags: () => void;
  onClose: () => void;
}
export function TagsEditor({
  open,
  tags,
  catalog,
  byId,
  loan,
  onAddTag,
  onUpdateTag,
  onRemoveTag,
  onMoveTag,
  onResetTags,
  onClose
}: TagsEditorProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(<div className="fixed inset-0 z-[9998] flex items-start justify-center bg-slate-900/30 p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-186" data-mp="1piab66let6f9">
      <div role="dialog" aria-modal="true" aria-label="Loan tags" className="mt-10 flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl" data-id="element-187" data-mp="wtv0txd83ca9">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-3" data-id="element-188" data-mp="2d7bnw619z3qc">
          <div data-id="element-189" data-mp="2i6fuqq0q8pdi">
            <h2 className="text-sm font-bold text-slate-900" data-id="element-190" data-mp="2i5wnc38pdjdq">Loan tags</h2>
            <p className="text-[12px] text-slate-500" data-id="element-191" data-mp="1siafzuqexd0s">
              Tags appear above the highlight fields. Show a tag on every loan, or only when a
              loan field meets a condition.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close tags editor" className="rounded p-1 text-slate-400 transition hover:bg-slate-50 hover:text-slate-700" data-id="element-192" data-mp="22gzz1qu1p3im">
            <XIcon className="h-4 w-4" data-id="element-193" data-mp="19skkf5etsn7t" />
          </button>
        </header>

        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2" data-id="element-194" data-mp="14thivuy20z4l">
          <p className="text-[12px] text-slate-500" data-id="element-195" data-mp="1jinpw534vtn1">
            {tags.filter(tag => isTagVisible(tag, loan, byId)).length} of {tags.length} showing
            on this loan
          </p>
          <div className="flex items-center gap-2" data-id="element-196" data-mp="r0i2lqqg70vz">
            <button type="button" onClick={onResetTags} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-800" data-id="element-197" data-mp="qz6wvldfzj28">
              <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-198" data-mp="32h2355nibwxa" />
              Reset
            </button>
            <button type="button" onClick={onAddTag} className="inline-flex items-center gap-1.5 rounded-md border border-[#cfe0f5] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0b5cab] transition hover:bg-[#f3f8ff]" data-id="element-199" data-mp="2nt7a1o5l7qjo">
              <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-200" data-mp="3ldtlncv8680a" />
              New tag
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-id="element-201" data-mp="37hpq9g8m7tm8">
          {tags.length === 0 ? <p className="py-10 text-center text-[13px] text-slate-500" data-id="element-202" data-mp="1w07xfbskoeeh">
              No tags yet. Add one to call out loan attributes at a glance.
            </p> : <ul className="space-y-2" data-id="element-203" data-mp="1zzp2i44379j4">
              {tags.map((tag, index) => {
            const showing = isTagVisible(tag, loan, byId);
            return <li key={tag.id} className="rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-id="element-204" data-mp="2bhg0i2nxzgr6" data-mpi={index}>
                    <div className="flex flex-wrap items-center gap-2" data-id="element-205" data-mp="21kjz0gwgnuw4">
                      <span className={['inline-flex h-6 shrink-0 items-center rounded border px-2 text-[12px] font-semibold', palette[tag.color].chip, palette[tag.color].text, tag.enabled ? '' : 'opacity-40'].join(' ')} data-id="element-206" data-mp="21i78vggjue7z">
                        {tag.label || 'Untitled'}
                      </span>
                      <input aria-label="Tag label" value={tag.label} onChange={event => onUpdateTag(tag.id, {
                  label: event.target.value
                })} className="w-44 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] font-medium text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-207" data-mp="1dvsembp44gtw" />
                      <div role="radiogroup" aria-label="Tag color" className="flex items-center gap-1" data-id="element-208" data-mp="3uf2rwk4it0oc">
                        {paletteOrder.map(color => {
                    const active = tag.color === color;
                    return <button key={color} type="button" role="radio" aria-checked={active} aria-label={palette[color].label} title={palette[color].label} onClick={() => onUpdateTag(tag.id, {
                      color
                    })} className={['h-5 w-5 rounded border-2 transition', palette[color].swatch, active ? 'border-slate-900' : 'border-white hover:border-slate-300'].join(' ')} data-id="element-209" data-mp="h32cgwg7ndyu" />;
                  })}
                      </div>
                      {showing ? <span className="rounded-full bg-[#0b5cab] px-2 py-0.5 text-[10.5px] font-bold text-white" data-id="element-210" data-mp="8vxae8p1bssk">
                          Showing
                        </span> : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10.5px] font-bold text-slate-600" data-id="element-211" data-mp="wnc63imb3buu">
                          Hidden
                        </span>}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-slate-600" data-id="element-212" data-mp="1b9rokmdhqs7o">
                      <select aria-label="When to show" value={tag.visibility} onChange={event => onUpdateTag(tag.id, {
                  visibility: event.target.value as LoanTag['visibility']
                })} className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] font-medium text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-213" data-mp="1g82z6zbm16qy">
                        <option value="always" data-id="element-214" data-mp="nkgiqqhaa7mo">Always show</option>
                        <option value="condition" data-id="element-215" data-mp="nkyr2lh80cay">Show when</option>
                      </select>
                      {tag.visibility === 'condition' ? <ConditionFields condition={tag.condition} catalog={catalog} byId={byId} onChange={patch => onUpdateTag(tag.id, {
                  condition: {
                    ...tag.condition,
                    ...patch
                  }
                })} data-id="element-216" data-mp="dmsqq8b2be87" /> : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-217" data-mp="f3pxvhq9mbsv">
                      <input aria-label="Tooltip description" value={tag.description ?? ''} placeholder="Tooltip description (optional)" onChange={event => onUpdateTag(tag.id, {
                  description: event.target.value
                })} className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-[12px] text-slate-800 focus:border-[#0b5cab] focus:outline-none" data-id="element-218" data-mp="b35zkatyh255" />
                      <button type="button" role="switch" aria-checked={tag.enabled} aria-label="Tag enabled" onClick={() => onUpdateTag(tag.id, {
                  enabled: !tag.enabled
                })} className={['relative h-4 w-8 shrink-0 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b5cab]', tag.enabled ? 'bg-[#0b5cab]' : 'bg-slate-300'].join(' ')} data-id="element-219" data-mp="3jl3ljc1u3kol">
                        <span aria-hidden="true" className={['absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', tag.enabled ? 'left-[18px]' : 'left-0.5'].join(' ')} data-id="element-220" data-mp="1pb5flvuq91rr" />
                      </button>
                      <div className="flex items-center" data-id="element-221" data-mp="11j8465v6wbr6">
                        <button type="button" disabled={index === 0} onClick={() => onMoveTag(index, index - 1)} aria-label="Move tag up" className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-222" data-mp="26agemx6wcmi1">
                          <ArrowUpIcon className="h-3.5 w-3.5" data-id="element-223" data-mp="1nmtj7kq6f67i" />
                        </button>
                        <button type="button" disabled={index === tags.length - 1} onClick={() => onMoveTag(index, index + 1)} aria-label="Move tag down" className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-30" data-id="element-224" data-mp="1skcwahb51cxv">
                          <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-225" data-mp="3n4de6hyjs87s" />
                        </button>
                        <button type="button" onClick={() => onRemoveTag(tag.id)} aria-label="Delete tag" className="rounded p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-700" data-id="element-226" data-mp="3j1qlkeu1yj9j">
                          <Trash2Icon className="h-3.5 w-3.5" data-id="element-227" data-mp="345ss54bijt10" />
                        </button>
                      </div>
                    </div>
                  </li>;
          })}
            </ul>}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-2.5" data-id="element-228" data-mp="2vabbfzw1axnq">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-50" data-id="element-229" data-mp="2qbrs878trzz2">
            Done
          </button>
        </footer>
      </div>
    </div>, document.body);
}